import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import "./ResultPage.scss";
import DOMPurify from "dompurify";
const ResultPage = () => {
  const [resultData, setResultData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeSection, setActiveSection] = useState(0);
  const navigate = useNavigate();
  const location = useLocation();
  const examId = localStorage.getItem("test_id");
  const username = localStorage.getItem("username");

  useEffect(() => {
    loadResultData();
  }, []);

  const loadResultData = () => {
    if (location.state?.resultData) {
      setResultData(location.state.resultData);
      setLoading(false);
      return;
    }

    const savedResult = localStorage.getItem(`exam_result_${examId}`);
    if (savedResult) {
      setResultData(JSON.parse(savedResult));
      setLoading(false);
    } else {
      navigate("/");
    }
  };

  // Calculate score with negative marking
  const calculateScore = () => {
    if (!resultData)
      return { totalScore: 0, totalObtained: 0, sectionWise: [] };

    let totalScore = 0;
    let totalObtained = 0;
    const sectionWise = [];

    resultData.examData.sections.forEach((section, sectionIndex) => {
      let sectionScore = 0;
      let sectionObtained = 0;
      let sectionCorrect = 0;
      let sectionWrong = 0;
      let sectionAttempted = 0;

      section.passages.forEach((passage, passageIndex) => {
        passage.questions.forEach((question, questionIndex) => {
          const globalIndex = getGlobalIndex(
            sectionIndex,
            passageIndex,
            questionIndex
          );
          const userAnswer = resultData.userAnswers[globalIndex]?.answer;
          const correctAnswer = question.correct_answer;
          const questionMarks = question.marks || 1;
          const negativeMarking = getNegativeMarking(question);

          totalScore += questionMarks;

          if (userAnswer && userAnswer !== "") {
            sectionAttempted++;
            if (userAnswer === correctAnswer) {
              sectionCorrect++;
              sectionObtained += questionMarks;
              totalObtained += questionMarks;
            } else {
              sectionWrong++;
              // Apply negative marking for wrong answer
              const negativeMarks = questionMarks * negativeMarking;
              sectionObtained -= negativeMarks;
              totalObtained -= negativeMarks;
            }
          }
        });
      });

      sectionWise.push({
        sectionTitle: section.section_title,
        totalQuestions: getSectionQuestionsCount(sectionIndex),
        attempted: sectionAttempted,
        correct: sectionCorrect,
        wrong: sectionWrong,
        score: sectionObtained,
        maxScore: sectionScore,
      });
    });

    return { totalScore, totalObtained, sectionWise };
  };

  // Get negative marking value for a question
  const getNegativeMarking = (question) => {
    // First check if question has specific negative marking
    if (
      question.negative_marking !== null &&
      question.negative_marking !== undefined
    ) {
      return parseFloat(question.negative_marking);
    }
    // Then check exam level negative marking
    if (resultData.examData.negative_marking) {
      return parseFloat(resultData.examData.negative_marking);
    }
    // Default no negative marking
    return 0;
  };

  // Get global index for a question
  const getGlobalIndex = (sectionIndex, passageIndex, questionIndex) => {
    let globalIndex = 0;
    for (let s = 0; s < sectionIndex; s++) {
      const section = resultData.examData.sections[s];
      for (let p = 0; p < section.passages.length; p++) {
        globalIndex += section.passages[p].questions.length;
      }
    }
    for (let p = 0; p < passageIndex; p++) {
      globalIndex +=
        resultData.examData.sections[sectionIndex].passages[p].questions.length;
    }
    globalIndex += questionIndex;
    return globalIndex;
  };

  // Get questions count for a section
  const getSectionQuestionsCount = (sectionIndex) => {
    if (!resultData?.examData?.sections[sectionIndex]) return 0;
    let count = 0;
    resultData.examData.sections[sectionIndex].passages.forEach((passage) => {
      count += passage.questions.length;
    });
    return count;
  };

  // Get questions for a specific section
  const getSectionQuestions = (sectionIndex) => {
    if (!resultData?.examData?.sections[sectionIndex]) return [];

    const sectionQuestions = [];
    resultData.examData.sections[sectionIndex].passages.forEach(
      (passage, passageIndex) => {
        passage.questions.forEach((question, questionIndex) => {
          sectionQuestions.push({
            ...question,
            sectionIndex,
            passageIndex,
            questionIndex,
            passageTitle: passage.passage_title,
            globalIndex: getGlobalIndex(
              sectionIndex,
              passageIndex,
              questionIndex
            ),
          });
        });
      }
    );
    return sectionQuestions;
  };

  const handleRetakeTest = () => {
    localStorage.removeItem(`exam_result_${examId}`);
    localStorage.removeItem(`exam_state_${examId}`);
    navigate("/");
  };

  const handleGoHome = () => {
    localStorage.removeItem(`exam_result_${examId}`);
     localStorage.removeItem(`exam_state_${examId}`);
    navigate("/");
  };


    if (loading) {
    return (
      <div className="exam-result-page">
        <div className="page_header"></div>
        <div className="result-loading">
          <div className="loading-spinner"></div>
          <h3>Analyzing Your Performance...</h3>
          <p>Preparing your detailed result report</p>
        </div>
      </div>
    );
  }

  if (!resultData) {
    return <div className="result-error">No result data found</div>;
  }

  const { totalScore, totalObtained, sectionWise } = calculateScore();
  const percentage =
    totalScore > 0 ? ((totalObtained / totalScore) * 100).toFixed(2) : 0;
  const isPassed = resultData.examData.passing_marks
    ? totalObtained >= resultData.examData.passing_marks
    : percentage >= 40;
  const negativeMarking = resultData.examData.negative_marking || 0;

  return (
    <div className="result-page">
      <div className="page_header"></div>

      <div className="result-content">
      <div className="result-header">
        <h4>Exam Results</h4>
        <button className="btn home-btn" onClick={handleGoHome}>
          Go to Home
        </button>
      </div>
        {/* Exam Summary */}
        <div className="exam-summary">
          <h5>{resultData.examData.title}</h5>
          {negativeMarking > 0 && (
            <div className="negative-marking-info">
              <span>
                ⚠️ Negative Marking: {negativeMarking * 100}% per wrong answer
              </span>
            </div>
          )}
          <div className="summary-cards">
            <div className="summary-card total">
              <div className="card-icon">📊</div>
              <div className="card-content">
                <h6>Total Questions</h6>
                <p>{resultData.totalQuestions}</p>
              </div>
            </div>

            <div className="summary-card answered">
              <div className="card-icon">✅</div>
              <div className="card-content">
                <h6>Answered</h6>
                <p>{resultData.answeredQuestions}</p>
              </div>
            </div>

            <div className="summary-card marked">
              <div className="card-icon">📌</div>
              <div className="card-content">
                <h6>Marked</h6>
                <p>{resultData.markedQuestions}</p>
              </div>
            </div>

            <div className="summary-card not-answered">
              <div className="card-icon">❌</div>
              <div className="card-content">
                <h6>Not Answered</h6>
                <p>{resultData.notAnsweredQuestions}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Score Card */}
        <div className={`score-card ${isPassed ? "passed" : "failed"}`}>
          <div className="score-header">
            <h6>Your Score</h6>
            <span className={`status-badge ${isPassed ? "passed" : "failed"}`}>
              {isPassed ? "PASSED" : "FAILED"}
            </span>
          </div>
          <div className="score-details">
            <div className="score-circle">
              <div className="score-percentage">{percentage}%</div>
              <div className="score-text">Score</div>
            </div>
            <div className="marks-details">
              <div className="marks-row">
                <span>Obtained Marks:</span>
                <span className="marks-value">
                  {/* {totalObtained.toFixed(2)}/{totalScore} */}
                  {Number(totalObtained).toPrecision(4)}/{String(Number(totalScore)).padStart(3, '0')}
                </span>
              </div>
              <div className="marks-row">
                <span>Passing Marks:</span>
                <span className="marks-value">
                  {resultData.examData.passing_marks || "Not specified"}
                </span>
              </div>
              {negativeMarking > 0 && (
                <div className="marks-row">
                  <span>Negative Marking:</span>
                  <span className="marks-value">
                    {negativeMarking * 100}% per wrong answer
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Section-wise Performance */}
        <div className="section-performance">
          <h6>Section-wise Performance</h6>
          <div className="section-cards">
            {sectionWise.map((section, index) => (
              <div key={index} className="section-card">
                <div className="section-header">
                  <h6>{section.sectionTitle}</h6>
                  <span className="section-score">
                    {section.score.toFixed(2)}
                  </span>
                </div>
                <div className="section-stats">
                  <div className="stat">
                    <span className="stat-label">Total:</span>
                    <span className="stat-value">{section.totalQuestions}</span>
                  </div>
                  <div className="stat">
                    <span className="stat-label">Attempted:</span>
                    <span className="stat-value">{section.attempted}</span>
                  </div>
                  <div className="stat">
                    <span className="stat-label correct">Correct:</span>
                    <span className="stat-value">{section.correct}</span>
                  </div>
                  <div className="stat">
                    <span className="stat-label wrong">Wrong:</span>
                    <span className="stat-value">{section.wrong}</span>
                  </div>
                </div>
                <button
                  className="view-details-btn"
                  onClick={() => setActiveSection(index)}
                >
                  View Details
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Detailed Results - Section-wise */}
        <div className="detailed-results">
          <div className="results-header">
            <h6>
              Detailed Analysis -{" "}
              {resultData.examData.sections[activeSection]?.section_title}
            </h6>
            <div className="section-tabs">
              {resultData.examData.sections.map((section, index) => (
                <button
                  key={index}
                  className={`section-tab ${
                    index === activeSection ? "active" : ""
                  }`}
                  onClick={() => setActiveSection(index)}
                >
                  {section.section_title}
                </button>
              ))}
            </div>
          </div>

          <div className="questions-list">
            {getSectionQuestions(activeSection).map((question, index) => {
              const userAnswer =
                resultData.userAnswers[question.globalIndex]?.answer ||
                "Not Answered";
              const correctAnswer = question.correct_answer || "N/A";
              const isCorrect = userAnswer === correctAnswer;
              const status =
                resultData.questionStatuses[question.globalIndex] ||
                "not_answered";
              const negativeMarking = getNegativeMarking(question);
              const questionMarks = question.marks || 1;

              return (
                <div key={question.id} className="question-result">
                  {/* Passage Header if available */}
                  {question.passageTitle && (
                    <div className="passage-header">
                      <strong>Passage:</strong> {question.passageTitle}
                    </div>
                  )}

                  <div className="question-header">
                    <span className="question-number">Q{index + 1}</span>
                    <span className={`status-dot ${status}`}></span>
                    <span
                      className={`answer-status ${
                        isCorrect ? "correct" : "incorrect"
                      }`}
                    >
                      {isCorrect ? "✓ Correct" : "✗ Incorrect"}
                    </span>
                    <span className="marks-info">
                      Marks: {questionMarks}
                      {negativeMarking > 0 &&
                        ` (Negative: -${negativeMarking * 100}%)`}
                    </span>
                  </div>

                  <div
                    className="question-text"
                    dangerouslySetInnerHTML={{
                      __html: DOMPurify.sanitize(question.question_text || ""),
                    }}
                  >
                    {/* {question.question_text} */}
                  </div>

                  <div className="answer-comparison">
                    <div className="answer-row">
                      <span className="answer-label">Your Answer:</span>
                      <span
                        className={`user-answer ${!isCorrect ? "wrong" : ""}`}
                      >
                        {userAnswer}
                      </span>
                    </div>
                    {!isCorrect && userAnswer !== "Not Answered" && (
                      <div className="answer-row">
                        <span className="answer-label">Correct Answer:</span>
                        <span className="correct-answer">{correctAnswer}</span>
                      </div>
                    )}
                    <div className="marks-obtained">
                      <span className="answer-label">Marks Obtained:</span>
                      <span
                        className={`marks-value ${
                          isCorrect
                            ? "positive"
                            : userAnswer !== "Not Answered"
                            ? "negative"
                            : "zero"
                        }`}
                      >
                        {isCorrect
                          ? `+${questionMarks}`
                          : userAnswer !== "Not Answered"
                          ? `-${(questionMarks * negativeMarking).toFixed(2)}`
                          : "0"}
                      </span>
                    </div>
                  </div>

                  {question.explanation && (
                    <div className="explanation">
                      <strong>Explanation:</strong> {question.explanation}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Performance Summary */}
        <div className="performance-summary">
          <h6>Performance Summary</h6>
          <div className="summary-grid">
            <div className="summary-item">
              <div className="summary-icon correct">✅</div>
              <div className="summary-text">
                <div className="summary-count">
                  {sectionWise.reduce(
                    (acc, section) => acc + section.correct,
                    0
                  )}
                </div>
                <div className="summary-label">Correct Answers</div>
              </div>
            </div>
            <div className="summary-item">
              <div className="summary-icon wrong">❌</div>
              <div className="summary-text">
                <div className="summary-count">
                  {sectionWise.reduce((acc, section) => acc + section.wrong, 0)}
                </div>
                <div className="summary-label">Wrong Answers</div>
              </div>
            </div>
            <div className="summary-item">
              <div className="summary-icon skipped">⏭️</div>
              <div className="summary-text">
                <div className="summary-count">
                  {resultData.totalQuestions - resultData.answeredQuestions}
                </div>
                <div className="summary-label">Skipped Questions</div>
              </div>
            </div>
            <div className="summary-item">
              <div className="summary-icon score">📈</div>
              <div className="summary-text">
                <div className="summary-count">{percentage}%</div>
                <div className="summary-label">Overall Score</div>
              </div>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="result-actions">
          <button className="btn retake-btn" onClick={handleRetakeTest}>
            Retake Test
          </button>
          <button className="educate-btn bg-primary" onClick={handleGoHome}>
            Back to Home
          </button>
        </div>
      </div>
    </div>
  );
};

export default ResultPage;
